import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getUserData,
  hasValidSessionToken,
  loginWithGoogle,
  type User,
} from '../services/authService';
import { handlePostLoginRedirect } from '../services/clientService';
import './GoogleCallback.css';

const CANCELLED_MESSAGE = 'Autenticación cancelada o fallida';
const CODE_NOT_FOUND_MESSAGE = 'Código de autenticación no encontrado';
const PROVIDER_UNAVAILABLE_MESSAGE =
  'No pudimos contactar al proveedor de autenticación. Por favor, intente nuevamente en unos minutos.';
const GENERIC_ERROR_MESSAGE =
  'Error al iniciar sesión con Google. Por favor, intente nuevamente.';

function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasStarted = useRef(false);

  useEffect(() => {
    // The authorization code is single-use, so this must run at most once per
    // mount — including the re-run caused by stripping `?code=` from the URL.
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;

    const goToSessionDestination = (user: User | null): boolean => {
      if (!user) {
        return false;
      }

      if (user.personas.length === 0) {
        navigate('/complete-profile');
      } else {
        handlePostLoginRedirect();
      }

      return true;
    };

    const resumeExistingSession = (): boolean => {
      if (!hasValidSessionToken()) {
        return false;
      }

      return goToSessionDestination(getUserData());
    };

    const failToLogin = (message: string) => {
      setError(message);
      setTimeout(() => navigate('/login'), 3000);
    };

    const handleGoogleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        failToLogin(CANCELLED_MESSAGE);
        return;
      }

      if (!code) {
        // No code to spend: either this run already cleaned the URL, or the
        // page was opened directly. A session from a previous attempt is the
        // normal case here, so recover from it before reporting an error.
        if (resumeExistingSession()) {
          return;
        }
        failToLogin(CODE_NOT_FOUND_MESSAGE);
        return;
      }

      // Consume the code by dropping it from the URL *before* the exchange, so
      // no reload, back navigation or restored tab can ever send it a second
      // time. Cognito rejects a replayed code and the login looks broken.
      navigate(location.pathname, { replace: true });

      try {
        const response = await loginWithGoogle(code);
        goToSessionDestination(response.session_information.user);
      } catch (err) {
        const loginErr = err as Error & { code?: string };

        if (loginErr.code === 'google_authorization_code_invalid') {
          // The code was already spent, which means the session it produced may
          // already be sitting in the shared cookies. Recover in silence rather
          // than showing an authentication error the user did not cause.
          if (resumeExistingSession()) {
            return;
          }
          navigate('/login');
          return;
        }

        console.error('Google callback error:', err);

        if (loginErr.code === 'google_auth_provider_unavailable') {
          failToLogin(PROVIDER_UNAVAILABLE_MESSAGE);
          return;
        }

        failToLogin(GENERIC_ERROR_MESSAGE);
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate, location.pathname]);

  return (
    <div className="callback-container">
      <div className="callback-content">
        {error ? (
          <>
            <div className="error-icon">⚠️</div>
            <h2>Error de Autenticación</h2>
            <p>{error}</p>
            <p className="redirect-message">Redirigiendo al inicio de sesión...</p>
          </>
        ) : (
          <>
            <div className="spinner"></div>
            <h2>Iniciando sesión con Google</h2>
            <p>Por favor espere...</p>
          </>
        )}
      </div>
    </div>
  );
}

export default GoogleCallback;
